/**
 * @file AdminMyClients.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useCallback, useEffect, useState } from "react";
import Widget from "../../widgets/Widget";
import SelectInput from "../../../../components/forms/SelectInput";
import SearchInput from "../../../../components/forms/SearchInput";
import TextInput from "../../../../components/forms/TextInput";
import { useForm } from "../../../../utils/useForm";
import Request from "../../../../utils/fetchUtils";
import DgButton from "../../../../components/DgButton";
import DgModal from "../../../../components/DgModal";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { useNavigate } from "react-router";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BlockIcon from '@mui/icons-material/Block';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import EmailIcon from "@mui/icons-material/Email";

const emptyInviteSchema = {
  fullName: "",
  email: "",
  phone: "",
};

const AdminMyClients = ({ }) => {
  const navigate = useNavigate();
  const [filters, setFilters, resetFilters] = useForm({
    assigned: "",
    advisor: "",
    search: "",
    userType: "",
  });
  const [passwordStatusData, setPasswordStatusData] = useState({});
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState({
    modalAssignmentState: false,
    modalInactivateState: false,
    modalReassignmentState: false,
    modalEditRiskState: false,
    customer: null
  });
  const [assignmentData, setAssignmentData, resetAssignmentData] = useForm({
    advisorKey: "",
    search: "",
  });
  const [reassignmentData, setReassignmentData, resetReassignmentData] = useForm({
    advisorKey: "",
    search: "",
  });
  // Nueva funcionalidad de invitación
  const [inviteData, setInviteData, resetInviteData] = useForm({
    ...emptyInviteSchema,
  });
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [advisors, setAdvisors] = useState({
    array: [],
    object: {},
  });
  const [customerToInactivate, setCustomerToInactivate] = useState(null);
  const [riskScoreInput, setRiskScoreInput] = useState("");
  const [riskScoreLoading, setRiskScoreLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Get user role to check permissions
  var user = JSON.parse(localStorage.session);
  const role = user.role;

  // Check if user can edit risk scores
  const canEditRiskScore = role === 'admin' || role === 'fundManager' || role === 'advisor';

  const isImportedUser = (customer) => {
    return customer.applications &&
      customer.applications.includes('imported-user');
  };

  const getUserType = (customer) => {
    return isImportedUser(customer) ? 'imported' : 'registered';
  };

  const handleInviteUser = async () => {
    const { fullName, email, phone } = inviteData;

    if (fullName !== "" && email !== "" && phone !== "") {
      setIsLoading(true);

      try {
        const res = await Request("/user/import", inviteData, "POST", {});
        if (res.ok) {
          Swal.fire({
            title: "Invitation sent successfully",
            text: "The user has been invited to the platform",
            icon: "success",
          }).then(() => {
            fetchData();
            setIsInviteModalOpen(false);
            resetInviteData();
          });
        } else {
          Swal.fire({
            title: "Error",
            text: res.error,
            icon: "error",
          }).then(() => {
            fetchData();
            setIsInviteModalOpen(false);
            resetInviteData();
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "Network error or unexpected error occurred",
          icon: "error",
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      Swal.fire({
        title: "Error",
        text: "All fields are required",
        icon: "error",
      });
    }
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const checkUserPasswordStatus = async (email) => {
    try {
      const res = await Request("/user/password-status", { email }, "POST",{});
      if (res.ok) {
        return res.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const resendExpiredPassword = async (customer) => {
    const { email } = customer;
    if (!email) {
      Swal.fire("Error", "Customer email not found", "error");
      return;
    }

    setIsCheckingPassword(true);

    try {
      const passwordStatus = await checkUserPasswordStatus(email);

      if (passwordStatus && passwordStatus.hasTemporaryPassword && !passwordStatus.isExpired) {
        const expiryDate = new Date(passwordStatus.expiryDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'America/New_York'
        });

        Swal.fire({
          title: "Password Still Valid",
          text: `Current password is valid until ${expiryDate}`,
          icon: "info"
        });
        setIsCheckingPassword(false);
        return;
      }


      const res = await Request("/user/resend-password", { email }, "POST");

      if (res.ok) {
        Swal.fire({
          title: "Password Sent",
          text: `New temporary password has been sent to ${email}`,
          icon: "success"
        });
      } else {
        Swal.fire({
          title: "Error",
          text: res.error || "Failed to send new password",
          icon: "error"
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Network error occurred",
        icon: "error"
      });
    } finally {
      setIsCheckingPassword(false);
    }
  };

  // Function to calculate risk difference and return appropriate indicator
  const getRiskDifferenceIndicator = (riskAssessment, weightedRisk) => {
    // Handle null, undefined, or empty values
    const riskScore = parseFloat(riskAssessment) || 0;
    const weightedScore = parseFloat(weightedRisk);

    // If weighted risk is null, undefined, or empty, don't show indicator
    if (weightedScore === null || weightedScore === undefined || weightedRisk === '' || isNaN(weightedScore)) {
      return null;
    }

    const difference = Math.abs(riskScore - weightedScore);

    if (difference < 15) {
      return {
        icon: <CheckCircleIcon style={{ fontSize: "18px", color: "#4CAF50" }} />,
        tooltip: `Risk difference: ${difference.toFixed(1)} (Good match)`,
        color: "#4CAF50"
      };
    } else if (difference >= 15 && difference <= 30) {
      return {
        icon: <WarningIcon style={{ fontSize: "18px", color: "#FF9800" }} />,
        tooltip: `Risk difference: ${difference.toFixed(1)} (Moderate difference)`,
        color: "#FF9800"
      };
    } else {
      return {
        icon: <ErrorIcon style={{ fontSize: "18px", color: "#F44336" }} />,
        tooltip: `Risk difference: ${difference.toFixed(1)} (High difference)`,
        color: "#F44336"
      };
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterData = () => {
    const { assigned, advisor, search, userType } = filters;
    let filtered = [...allCustomers];

    if (assigned !== "") {
      filtered = filtered.filter((c) =>
        assigned === "1"
          ? c.advisorOwner && c.advisorOwner !== ""
          : !c.advisorOwner || c.advisorOwner === ""
      );
    }

    if (advisor !== "") {
      filtered = filtered.filter((c) => c.advisorOwner === advisor);
    }

    // Nuevo filtro para tipo de usuario
    if (userType !== "") {
      filtered = filtered.filter((c) => {
        const customerType = getUserType(c);
        return customerType === userType;
      });
    }

    if (search && search.trim() !== "") {
      const query = search.toLowerCase();
      filtered = filtered.filter((c) =>
        c.fullName?.toLowerCase().includes(query)
      );
    }

    setCustomers(filtered);
  };

  const handleResetFilters = () => {
    resetFilters();
    setCustomers(allCustomers);
  };

  // Risk Score editing functions
  const handleEditRiskScore = (customer) => {
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: false,
      modalEditRiskState: true,
      customer: customer
    });
    setRiskScoreInput(customer.score || "");
  };

  const updateCustomerRiskScore = async () => {
    const score = parseFloat(riskScoreInput);

    // Validation
    if (!riskScoreInput || isNaN(score) || score < 0) {
      Swal.fire(
        "Invalid Input!",
        "Please enter a valid risk score greater than or equal to 0",
        "warning"
      );
      return;
    }

    setRiskScoreLoading(true);
    try {
      const res = await Request(
        '/user/score',
        {
          userId: selectedCustomer.customer.userId,
          score: score,
          SK: selectedCustomer.customer.SK
        },
        'PUT'
      );

      setRiskScoreLoading(false);

      if (res.ok) {
        setSelectedCustomer({
          modalAssignmentState: false,
          modalInactivateState: false,
          modalReassignmentState: false,
          modalEditRiskState: false,
          customer: null
        });

        Swal.fire('Updated!', 'Customer risk score has been updated successfully', 'success');

        // Refresh clients list to show updated score
        fetchCustomers();
      } else {
        Swal.fire('Error', 'Could not update customer risk score', 'error');
      }
    } catch (error) {
      setRiskScoreLoading(false);
      Swal.fire('Error', 'An error occurred while updating customer risk score', 'error');
    }
  };

  const handleSelectCustomerForAssignment = (customer) => {
    resetAssignmentData();
    resetReassignmentData();
    setSelectedCustomer({
      modalAssignmentState: true,
      modalInactivateState: false,
      modalReassignmentState: false,
      modalEditRiskState: false,
      customer: customer
    });
  };

  const handleSelectCustomerForBrokers = (customer) => {
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: false,
      modalEditRiskState: false,
      customer: customer
    });
  };

  const generateAuthorizeUrl = () => {
    const scope = "read,trade,market";
    const { REACT_APP_TRADIER_CLIENT_ID, REACT_APP_TRADIER_CLIENT_SC } =
      process.env;
    return `https://api.tradier.com/v1/oauth/authorize?client_id=${REACT_APP_TRADIER_CLIENT_ID}&scope=${scope}&state=${REACT_APP_TRADIER_CLIENT_SC}`;
  };

  const fetchCustomers = async () => {
    const res = await Request("/trading/my-clients");
    if (res.ok) {
      // ordenar por nombre alfabéticamente
      const sortedCustomers = res.data.sort((a, b) => {
        const nameA = a.fullName?.toLowerCase() || "";
        const nameB = b.fullName?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });
      setCustomers(sortedCustomers);
      setAllCustomers(sortedCustomers);
    }
  };

  const fetchAdvisors = async () => {
    const res = await Request("/user?role=advisor");
    if (res.ok) {
      const objectAdvisors = {};
      res.data.forEach((advisor) => {
        objectAdvisors[advisor.SK] = advisor;
      });
      setAdvisors({ array: res.data, object: objectAdvisors });
    }
  };

  const postAdvisorAssignment = async () => {
    if (!selectedCustomer.customer || !selectedCustomer.customer.SK) {
      Swal.fire("Error", "Invalid client or incomplete information", "error");
      return;
    }

    if (!assignmentData.advisorKey) {
      Swal.fire("Error", "Please select an advisor", "error");
      return;
    }

    const res = await Request(
      "/trading/my-clients",
      {
        customerKey: selectedCustomer.customer.SK,
        advisorKey: assignmentData.advisorKey,
      },
      "POST"
    );
    if (res.ok) {
      Swal.fire("Success", "User successfully assigned", "success").then(() => {
        fetchCustomers();
        resetFilters();
        resetAssignmentData();
      });
    } else {
      Swal.fire("Something happened ...", res.error, "error");
    }
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: false,
      modalEditRiskState: false,
      customer: null
    });
  };

  const handleReassignAdvisor = async () => {
    if (!reassignmentData.advisorKey) {
      Swal.fire("Error", "Please select a new advisor", "error");
      return;
    }

    if (!selectedCustomer.customer || !selectedCustomer.customer.SK) {
      Swal.fire("Error", "Invalid client or incomplete information", "error");
      return;
    }

    if (reassignmentData.advisorKey === selectedCustomer.customer.advisorOwner) {
      Swal.fire("Error", "Please select a different advisor than the current one", "error");
      return;
    }

    try {
      const res = await Request(
        "/user/reassign-advisor",
        {
          clientKey: selectedCustomer.customer.SK,
          advisorKey: reassignmentData.advisorKey,
        },
        "POST"
      );

      if (res.ok) {
        Swal.fire("Success", "Advisor successfully reassigned", "success").then(() => {
          fetchCustomers();
          resetReassignmentData();
          setSelectedCustomer({
            modalAssignmentState: false,
            modalInactivateState: false,
            modalReassignmentState: false,
            modalEditRiskState: false,
            customer: null
          });
        });
      } else {
        Swal.fire("Error", res.message || "Failed to reassign advisor", "error");
      }
    } catch (error) {
      console.error("Error during reassignment:", error);
      Swal.fire("Error", "An unexpected error occurred during reassignment", "error");
    }
  };

  const openReassignModal = (customer) => {
    if (!customer) {
      console.error("Customer object is undefined in openReassignModal");
      return;
    }

    resetReassignmentData();
    resetAssignmentData();

    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: true,
      modalEditRiskState: false,
      customer: customer
    });
  };

  const disableReassignButton = () => {
    return !reassignmentData.advisorKey ||
      !selectedCustomer.customer ||
      !selectedCustomer.customer.SK ||
      reassignmentData.advisorKey === selectedCustomer.customer.advisorOwner;
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    await fetchCustomers();
    await fetchAdvisors();
    setIsLoading(false);
  }, []);

  const handleViewClientDetails = (customer) => {
    const userRoute = `/dashboard/customer/${customer.SK}`;
    navigate(userRoute, { state: customer });
  };

  const getPerformance = (customer) => {
    return typeof customer?.timeWeightedPerformance === "number"
      ? customer.timeWeightedPerformance.toFixed(2)
      : "-";
  };

  const openInactivateModal = (customer) => {
    setCustomerToInactivate(customer);
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: true,
      modalReassignmentState: false,
      modalEditRiskState: false,
      customer: customer
    });
  };

  const handleInactivateUser = async () => {
    if (customerToInactivate) {
      setIsLoading(true);
      const res = await Request(`user/${customerToInactivate.userId}`, {}, "DELETE", {
        Userkey: customerToInactivate.SK,
      });
      setIsLoading(false);
      if (res.ok) {
        Swal.fire("Success", "User successfully inactivated", "success").then(() => {
          fetchData();
          setSelectedCustomer({
            modalAssignmentState: false,
            modalInactivateState: false,
            modalReassignmentState: false,
            modalEditRiskState: false,
            customer: null
          });
        });
      } else {
        Swal.fire("Error", res.message || "We couldn't inactivate the user", "error");
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterData();
  }, [filters, filterData]);

  return (
    <div>
      <Widget name={"my-clients"} width={"full-width"}>
        <div className="toppers">
          <div className="filters">
            <div>
              <div className="filter-title form-field">
                <SelectInput
                  customInput={{
                    label: "Filter by assigned",
                    name: "assigned",
                    value: filters.assigned,
                    action: setFilters,
                  }}
                  data={[
                    { value: "", text: "All" },
                    { value: "1", text: "Assigned" },
                    { value: "0", text: "Non assigned" },
                  ]}
                />
              </div>
            </div>
            <div>
              <div className="filter-title form-field">
                <SelectInput
                  customInput={{
                    label: "Filter by advisor",
                    name: "advisor",
                    value: filters.advisor,
                    action: setFilters,
                  }}
                  data={[
                    { value: "", text: "Advisor" },
                    ...advisors.array.map((adv) => ({
                      value: adv.SK,
                      text: adv.fullName,
                    })),
                  ]}
                />
              </div>
            </div>
            <div>
              <div className="filter-title form-field">
                <SelectInput
                  customInput={{
                    label: "Filter by user type",
                    name: "userType",
                    value: filters.userType,
                    action: setFilters,
                  }}
                  data={[
                    { value: "", text: "All Users" },
                    { value: "registered", text: "Registered Users" },
                    { value: "imported", text: "Imported Users" },
                  ]}
                />
              </div>
            </div>
            <div>
              <div className="filter-title form-field">
                <SearchInput
                  customInput={{
                    label: "Search by name",
                    name: "search",
                    value: filters.search,
                    action: setFilters,
                    tooltipBool: true,
                    tooltipText: "Search for a client by name",
                    placeholder: "Type a name...",
                  }}
                />
              </div>
            </div>
            <div className="users-management">
              <div className="container-invite-filter">
                <button
                  onClick={handleResetFilters}
                  className="button reset-filters-btn"
                >
                  Reset Filters
                </button>
                <button
                  onClick={handleOpenInviteModal}
                  className="button invite-user-btn"
                >
                  Invite to Platform
                </button>
              </div>
            </div>
          </div>
        </div>

        <table className="clients-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>User Type</th>
              <th>Risk Assessment</th>
              <th>Weighted Risk</th>
              <th>Risk Match</th>
              <th>Performance</th>
              <th># Strategies</th>
              <th>% Allocated</th>
              <th>Advisor</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={10}>
                  <Loader />
                </td>
              </tr>
            ) : (
              <>
                {customers.length > 0 ? (
                  customers.map((customer) => {
                    const riskIndicator = getRiskDifferenceIndicator(customer.score, customer.weightedRiskScore);
                    const imported = isImportedUser(customer);
                    return (
                      <tr key={customer.userId}>
                        <td className="name">
                          <div className="user">
                            <div>
                              <span className="name">{customer.fullName}</span>
                              <span className="activity">
                                Last activity: {customer.updatedAt}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="user-type" style={{ textAlign: 'center' }}>
                          {imported ? (
                            <Tooltip title="Imported User" placement="top">
                              <Chip
                                icon={<PersonAddAltIcon style={{ fontSize: '16px', color: '#fff' }} />}
                                label="Imported"
                                size="small"
                                style={{
                                  backgroundColor: '#FF9800',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '11px'
                                }}
                              />
                            </Tooltip>


                          ) : (
                            <Chip
                              label="Registered"
                              size="small"
                              style={{
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '11px'
                              }}
                            />
                          )}
                        </td>
                        <td className="score" style={{ position: 'relative' }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}>
                            <p className="score-text">({customer.score || 0})</p>
                            {canEditRiskScore && (
                              <Tooltip title="Edit Risk Score" placement="top">
                                <EditIcon
                                  style={{
                                    fontSize: "16px",
                                    cursor: "pointer",
                                    color: "#3357FF",
                                    opacity: 0.7
                                  }}
                                  onClick={() => handleEditRiskScore(customer)}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td className="weighted-risk">
                          <span>
                            {customer.weightedRiskScore != null && customer.weightedRiskScore !== ''
                              ? customer.weightedRiskScore
                              : '-'}
                          </span>
                        </td>
                        <td className="risk-match" style={{ textAlign: 'center' }}>
                          {riskIndicator ? (
                            <Tooltip title={riskIndicator.tooltip} placement="top">
                              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                {riskIndicator.icon}
                              </div>
                            </Tooltip>
                          ) : (
                            <span style={{ color: '#666' }}>-</span>
                          )}
                        </td>
                        <td className="performance">
                          <span
                            style={{
                              color:
                                customer.timeWeightedPerformance > 0
                                  ? "#ff5e5b"
                                  : "#75a180",
                            }}
                          >
                            {getPerformance(customer)}%
                          </span>
                        </td>
                        <td className="strategy">
                          <span className="strategy-title">
                            {customer.strategies?.length || 0}
                          </span>
                        </td>
                        <td className="account">
                          {customer.usedPercentage !== undefined &&
                            customer.usedPercentage !== null
                            ? `${customer.usedPercentage}%`
                            : customer.usedPercentage}
                        </td>
                        <td className="account">
                          {customer.advisorOwner
                            ? advisors.object[customer.advisorOwner]?.fullName
                            : "Not assigned"}
                        </td>
                        <td className="fees">
                          {(!customer.advisorOwner ||
                            customer.advisorOwner === "") ? (
                            <Tooltip title="Assign Advisor" placement="top-start">
                              <IconButton
                                onClick={() =>
                                  handleSelectCustomerForAssignment(customer)
                                }
                              >
                                <PersonAddIcon style={{ color: "white" }} />
                              </IconButton>
                            </Tooltip>) : (
                            <Tooltip title="Reassign Advisor" placement="top-start">
                              <IconButton
                                onClick={() => openReassignModal(customer)}
                              >
                                <TransferWithinAStationIcon style={{ color: "#ABECD6" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {customer && customer?.brokerAccounts ? (
                            <Tooltip title="View Details" placement="top-start">
                              <IconButton
                                onClick={() => handleViewClientDetails(customer)}
                              >
                                <VisibilityIcon sx={{ color: "white" }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Inactivate User" placement="top-start">
                            <IconButton
                              onClick={() => openInactivateModal(customer)}
                            >
                              <BlockIcon style={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                          {imported && (!customer.brokerAccounts || !customer.brokerAccounts.length) && (
                            
                            <Tooltip title="Resend Password" placement="top-start">
                              <IconButton
                                onClick={() => resendExpiredPassword(customer)}
                                disabled={isCheckingPassword}
                              >
                                <EmailIcon style={{ color: "#FFB74D" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10}>No registered customers</td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </Widget>

      {/* Modal para invitar usuarios */}
      <DgModal
        modalState={isInviteModalOpen}
        handleModalState={() => {
          setIsInviteModalOpen(!isInviteModalOpen);
        }}
      >
        <h1 className="title">Invite to Platform</h1>
        <div className="strategy-content">
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Full name",
                name: "fullName",
                tooltipBool: false,
                value: inviteData.fullName,
                action: (e) => {
                  setInviteData(e);
                },
              }}
            />
          </div>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Email",
                name: "email",
                tooltipBool: false,
                value: inviteData.email,
                type: "email",
                action: (e) => {
                  setInviteData(e);
                },
              }}
            ></TextInput>
          </div>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Phone",
                name: "phone",
                tooltipBool: false,
                value: inviteData.phone,
                action: (e) => {
                  setInviteData(e);
                },
              }}
            ></TextInput>
          </div>
        </div>
        <DgButton
          category="primary"
          size="large"
          text="Send Invitation"
          action={handleInviteUser}
        ></DgButton>
      </DgModal>

      {/* Edit Risk Score Modal */}
      {canEditRiskScore && (
        <DgModal
          modalState={selectedCustomer.modalEditRiskState}
          handleModalState={() => setSelectedCustomer({
            ...selectedCustomer,
            modalEditRiskState: false
          })}
        >
          <h1 className="title">Edit Customer Risk Score</h1>
          <div className="customer-info" style={{ marginBottom: "20px", color: "#95b2c5" }}>
            <h3>{selectedCustomer.customer?.fullName}</h3>
            <p>Current Risk Score: {selectedCustomer.customer?.score || 0}</p>
          </div>
          <div className="strategy-content">
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Risk Score",
                  name: "riskScore",
                  type: "number",
                  tooltipBool: true,
                  tooltipText: "Enter a risk score value (minimum 0)",
                  value: riskScoreInput,
                  action: (e) => {
                    setRiskScoreInput(e.target.value);
                  },
                  min: 0,
                  step: 0.1
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
            <DgButton
              category="secondary"
              size="large"
              text="Cancel"
              action={() => setSelectedCustomer({
                ...selectedCustomer,
                modalEditRiskState: false
              })}
            />
            <DgButton
              category="primary"
              size="large"
              text="Update Risk Score"
              disabled={riskScoreLoading}
              action={updateCustomerRiskScore}
            />
          </div>
        </DgModal>
      )}

      <DgModal
        modalState={selectedCustomer.modalAssignmentState}
        handleModalState={() => {
          if (selectedCustomer.modalAssignmentState) {
            resetAssignmentData();
            setSelectedCustomer({
              ...selectedCustomer,
              modalAssignmentState: false,
              customer: selectedCustomer.customer
            });
          } else {
            setSelectedCustomer({
              ...selectedCustomer,
              modalAssignmentState: true,
            });
          }
        }}
      >
        <h1 className="title">Assign advisor</h1>
        <div className="strategy-content">
          <p style={{ marginBottom: '20px', color: '#95B2C5', textAlign: 'center' }}>
            You are about to assign an advisor to this client:<br />
            <strong>{selectedCustomer.customer?.fullName || "Cliente seleccionado"}</strong>
          </p>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Advisor",
                name: "advisorKey",
                value: assignmentData.advisorKey,
                action: setAssignmentData,
              }}
              data={[
                { value: "", text: "Choose an advisor" },
                ...advisors.array.map((adv) => ({
                  value: adv.SK,
                  text: adv.fullName,
                })),
              ]}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <DgButton
            category="secondary"
            size="large"
            text="Cancel"
            action={() => {
              resetAssignmentData();
              setSelectedCustomer({
                ...selectedCustomer,
                modalAssignmentState: false,
              });
            }}
          />
          <DgButton
            category="primary"
            size="large"
            text="Assign"
            action={postAdvisorAssignment}
            disabled={!assignmentData.advisorKey}
          />
        </div>
      </DgModal>

      <DgModal
        modalState={selectedCustomer.modalReassignmentState}
        handleModalState={() => {
          setSelectedCustomer({
            ...selectedCustomer,
            modalReassignmentState: !selectedCustomer.modalReassignmentState,
          });
        }}
      >
        <h1 className="title">Reassign Advisor</h1>
        <div className="strategy-content">
          <p style={{ marginBottom: '20px', color: '#95B2C5', textAlign: 'center' }}>
            You are about to reassign an advisor to this client:<br />
            <strong>{selectedCustomer.customer?.fullName || "Cliente seleccionado"}</strong><br />
            <span>
              Current advisor: {(selectedCustomer.customer?.advisorOwner && advisors.object[selectedCustomer.customer.advisorOwner])
                ? advisors.object[selectedCustomer.customer.advisorOwner].fullName
                : "Not assigned"}
            </span>
          </p>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "New Advisor",
                name: "advisorKey",
                value: reassignmentData.advisorKey,
                action: setReassignmentData,
              }}
              data={[
                { value: "", text: "Choose a new advisor" },
                ...advisors.array.map((adv) => ({
                  value: adv.SK,
                  text: adv.fullName,
                  disabled: selectedCustomer.customer && adv.SK === selectedCustomer.customer.advisorOwner,
                })),
              ]}
            />
            {selectedCustomer.customer && reassignmentData.advisorKey === selectedCustomer.customer.advisorOwner && (
              <p style={{ color: '#ff5e5b', fontSize: '12px', marginTop: '5px' }}>
                Please select a different advisor than the current one
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <DgButton
            category="secondary"
            size="large"
            text="Cancel"
            action={() => setSelectedCustomer({
              ...selectedCustomer,
              modalReassignmentState: false,
            })}
          />
          <DgButton
            category="primary"
            size="large"
            text="Reassign"
            action={handleReassignAdvisor}
            disabled={disableReassignButton()}
          />
        </div>
      </DgModal>

      <DgModal
        modalState={selectedCustomer.modalInactivateState}
        handleModalState={() => {
          setSelectedCustomer({
            ...selectedCustomer,
            modalInactivateState: !selectedCustomer.modalInactivateState,
          });
        }}
      >
        <h1 className="title">Deactivate User</h1>
        <div className="strategy-content">
          <p style={{ marginBottom: '20px', color: '#95B2C5', textAlign: 'center' }}>
            Are you sure you want to inactivate the user?<br />
            <strong>{customerToInactivate?.fullName}</strong><br />
            <span style={{ fontSize: '14px', color: '#FF5E5B' }}>
              This action cannot be undone.
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <DgButton
            category="secondary"
            size="large"
            text="Cancel"
            action={() => setSelectedCustomer({
              ...selectedCustomer,
              modalInactivateState: false,
            })}
          />
          <DgButton
            category="danger"
            size="large"
            text="Inactivate"
            action={handleInactivateUser}
            style={{ backgroundColor: '#FF5E5B', color: 'white', border: '1px solid #FF5E5B' }}
          />
        </div>
      </DgModal>
    </div>
  );
};

AdminMyClients.propTypes = {};

export default AdminMyClients;
